import type { ReactNode } from 'react';
import { shouldOutputPreviewAudio } from '../../engine/audioGate';
import { getPreviewAudioEngine } from '../../engine/previewAudio';
import { formatTimecode, projectDuration } from '../../timeline/ops';
import { useEditorStore } from '../../state/editorStore';
import { IconButton } from '../controls/PanelShell';

function TransportBtn({
  title,
  onClick,
  active,
  children,
  play,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  children: ReactNode;
  play?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={
        play
          ? 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--cg-text-high)] text-[var(--cg-bg-base)] shadow-sm hover:opacity-90'
          : `inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-1.5 text-xs transition-colors ${
              active
                ? 'bg-raised text-accent'
                : 'text-text-high hover:bg-raised hover:text-text-high'
            }`
      }
    >
      {children}
    </button>
  );
}

/** Bottom-center viewer transport: playhead controls + markers. */
export function ViewerTransport() {
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const togglePlay = useEditorStore((s) => s.togglePlay);
  const stepFrame = useEditorStore((s) => s.stepFrame);
  const goToStart = useEditorStore((s) => s.goToStart);
  const goToEnd = useEditorStore((s) => s.goToEnd);
  const addMarker = useEditorStore((s) => s.addMarker);
  const removeMarker = useEditorStore((s) => s.removeMarker);
  const jumpToMarker = useEditorStore((s) => s.jumpToMarker);
  const setInPoint = useEditorStore((s) => s.setInPoint);
  const setOutPoint = useEditorStore((s) => s.setOutPoint);
  const inPoint = useEditorStore((s) => s.inPoint);
  const outPoint = useEditorStore((s) => s.outPoint);
  const fps = project.settings.frameRate;
  const duration = projectDuration(project);
  const markers = project.markers ?? [];
  const markerAtPlayhead = markers.find((m) => Math.abs(m.time - playhead) < 1 / fps / 2);

  const kickAudio = (playing: boolean) => {
    const s = useEditorStore.getState();
    const output = shouldOutputPreviewAudio(s);
    getPreviewAudioEngine().sync(s.project, s.playhead, playing && output);
  };

  return (
    <div className="flex shrink-0 flex-col border-t border-hairline bg-panel">
      <div className="px-4 pt-2.5">
        <input
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={1 / fps}
          value={Math.min(playhead, duration)}
          onChange={(e) => {
            useEditorStore.getState().setPlaying(false);
            useEditorStore.getState().setPlayhead(Number(e.target.value));
          }}
          className="w-full"
          aria-label="Playhead"
        />
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 pb-2.5 pt-1">
        <span className="timecode text-xs text-text-low">
          {formatTimecode(playhead, fps)}
          <span className="mx-1.5 text-text-low/50">/</span>
          {formatTimecode(duration, fps)}
        </span>

        <div className="flex items-center justify-center gap-0.5">
          <TransportBtn title="Go to start (Home)" onClick={goToStart}>
            ⏮
          </TransportBtn>
          <TransportBtn title="Previous frame (←)" onClick={() => stepFrame(-1)}>
            ‹
          </TransportBtn>
          <TransportBtn
            title="Play / Pause (Space)"
            play
            onClick={() => {
              const next = !isPlaying;
              togglePlay();
              kickAudio(next);
            }}
          >
            {isPlaying ? '❚❚' : '▶'}
          </TransportBtn>
          <TransportBtn title="Next frame (→)" onClick={() => stepFrame(1)}>
            ›
          </TransportBtn>
          <TransportBtn title="Go to end" onClick={goToEnd}>
            ⏭
          </TransportBtn>
        </div>

        <div className="flex items-center justify-end gap-0.5">
          <TransportBtn
            title="Mark In (I)"
            active={inPoint != null && Math.abs(inPoint - playhead) < 1 / fps}
            onClick={() => setInPoint(playhead)}
          >
            In
          </TransportBtn>
          <TransportBtn
            title="Mark Out (O)"
            active={outPoint != null && Math.abs(outPoint - playhead) < 1 / fps}
            onClick={() => setOutPoint(playhead)}
          >
            Out
          </TransportBtn>
          {markerAtPlayhead ? (
            <TransportBtn
              title="Remove marker at playhead"
              onClick={() => removeMarker(markerAtPlayhead.id)}
            >
              ✕
            </TransportBtn>
          ) : (
            <TransportBtn title="Add marker (M)" onClick={() => addMarker()}>
              ◆
            </TransportBtn>
          )}
          <IconButton
            title={`${markers.length} marker(s) — click to jump next`}
            onClick={() => jumpToMarker(1)}
          >
            <span className="text-2xs text-text-low">{markers.length}</span>
          </IconButton>
        </div>
      </div>
    </div>
  );
}
